import {MithrilComponent} from '@bitstillery/mithril'
import {Button, Icon, Spinner} from '@bitstillery/common/components'
import {activated_filters, is_filter_activated, clear_filter} from '@bitstillery/common/lib/filters'
import {$t} from '@bitstillery/common/app'
import m from '@bitstillery/mithril'
import type {CollectionProxy} from '@bitstillery/common/lib/collection'
import type {Filter, FilterMultipleOption} from '@bitstillery/common/types'

/** What a filter button names: an option's label, an id without one, a search term, a range. */
type FilterTitle = string | number | undefined

/**
 * Only an id-based selection needs its filter's options to name itself, so only that is worth waiting
 * for — a search term or a range already carries its own label and must show immediately.
 *
 * Derived per render rather than counted: the previous version subscribed to each filter's `loading`
 * at mount and counted the transitions down to zero, so one flag that was never cleared (`query()`
 * returns early when the filter-metadata call 404s) left the whole widget invisible for the rest of
 * the session, with nothing on screen to say why.
 */
function filters_naming_pending(collection: CollectionProxy): boolean {
    return Object.values(collection.filters).some(
        (filter) =>
            filter.loading &&
            (filter.type === 'SELECT_MULTIPLE' || filter.type === 'SELECT_SINGLE') &&
            !filter.options.length &&
            is_filter_activated(filter),
    )
}

interface FiltersActiveAttrs {
    collection: CollectionProxy
}

export class FiltersActive extends MithrilComponent<FiltersActiveAttrs> {
    matching_option(id: unknown, options: FilterMultipleOption[]): FilterMultipleOption | undefined {
        for (const option of options) {
            if (option[0] === id) {
                return option
            }

            if (option.length === 4 && option[3].length) {
                const match = this.matching_option(id, option[3])
                if (match) return match
            }
        }
        return undefined
    }

    render_button(filter: Filter, title: FilterTitle, item_id: string | number | null) {
        const i18n = 'i18n' in filter ? filter.i18n : undefined
        return (
            <Button
                className='term'
                onclick={() => {
                    if (filter.type === 'TEXT' && Array.isArray(filter.selection)) {
                        // Written back into the selection it was filtered from.
                        const selection = filter.selection as unknown[]
                        const filtered = selection.filter((i) => i !== title)
                        selection.splice(0, selection.length, ...filtered)
                        return
                    }

                    // Clicking an active filter means deselecting the whole filter
                    // in case of a single value filter.
                    if (filter.type !== 'SELECT_MULTIPLE') {
                        clear_filter(filter)
                        return
                    }

                    // A multi filter will remove the clicked value from its selected options.
                    // Prevents redraws while manipulating the selection.
                    const selection: (string | number)[] = [...filter.selection]
                    let matched_option: FilterMultipleOption | undefined

                    for (const option of filter.options) {
                        // Clicked item_id has a direct match
                        if (option[0] === item_id) {
                            matched_option = option
                        } else if (option.length === 4) {
                            // Clicked item_id matches one of the children in options
                            matched_option = option[3].find((i) => i[0] === item_id)
                        }

                        if (matched_option) {
                            break
                        }
                    }

                    if (matched_option && matched_option.length === 4) {
                        // The matched option has children; remove those from the selection as well
                        for (const option of matched_option[3]) {
                            const sub_option_index = selection.indexOf(option[0])
                            if (sub_option_index >= 0) {
                                selection.splice(sub_option_index, 1)
                            }
                        }
                    }

                    selection.splice(
                        selection.findIndex((i) => i === item_id),
                        1,
                    )
                    ;(filter.selection as unknown[]).splice(0, filter.selection.length, ...selection)
                }}
                tip={() => {
                    const name = $t(filter.title)
                    let selection = title
                    return $t('filters.actions.clear_filter_selection', {name, selection})
                }}
                type='info'
                variant='unset'
            >
                <Icon
                    className='mr-05'
                    name={(() => {
                        if (filter.icon) {
                            return filter.icon
                        }
                        return 'filter'
                    })()}
                    size='s'
                    type='unset'
                />
                <span>
                    {i18n ? $t(`${i18n.prefix}${i18n.label ? i18n.label(String(title)) : String(title)}`) : title}
                    <Icon className='icon-close' name='filterRemove' />
                </span>
            </Button>
        )
    }

    render_filter(filter: Filter) {
        if (!is_filter_activated(filter)) {
            return null
        }

        // See if we can match the filter title from an active option
        let matched_option_index

        if (filter.type === 'SELECT_MULTIPLE') {
            const selected: unknown[] = filter.selection
            let item_ids: (string | number)[]
            const has_children = filter.options.some((i) => i.length >= 4)
            if (has_children) {
                // Remove selected children, if a parent is selected.
                item_ids = filter.options.flatMap((i) => {
                    const ids: (string | number)[] = []
                    if (selected.includes(i[0])) {
                        // Parent is selected; no need to add the children.
                        ids.push(i[0])
                    } else if (i.length === 4) {
                        // Parent is not selected; add the children as filter buttons.
                        const subids = i[3].filter((_i) => selected.includes(_i[0])).map((_i) => _i[0])
                        ids.push.apply(ids, subids)
                    }
                    return ids
                })
            } else {
                item_ids = [...filter.selection]
            }

            return item_ids.map((item_id) => {
                let title: FilterTitle = item_id
                if (filter.options.length) {
                    const matched_option = this.matching_option(item_id, filter.options)
                    if (matched_option) {
                        title = matched_option[1]
                    }
                }
                return this.render_button(filter, title, item_id)
            })
        } else if (filter.type === 'TEXT') {
            if (Array.isArray(filter.selection)) {
                const unique_selections = [...new Set<string | number>(filter.selection)]
                return unique_selections.map((title) => {
                    return this.render_button(filter, title, null)
                })
            }

            return this.render_button(filter, filter.selection, null)
        } else {
            let title: FilterTitle
            // Range filter
            if (filter.type === 'SELECT_RANGE') {
                const range: number[] = filter.selection
                const from = range[0]
                let to = range[1] === Infinity ? '∞' : range[1]

                let i18n_unit = $t(`filters.units.${filter.unit}`, {count: range[1]})
                if (range[1] === Infinity) {
                    i18n_unit = $t(`filters.units.${filter.unit}`, {count: 2})
                }

                title = `${String(from)} - ${String(to)} ${i18n_unit}`
            } else if (filter.type === 'SELECT_SINGLE') {
                // Try to match the selected id with a name in options
                if (Array.isArray(filter.selection)) {
                    // Summary of a multiple selection when using single mode
                    title = `${filter.title}: ${filter.selection.length}`
                } else {
                    matched_option_index = filter.options.findIndex((i) => i[0] === filter.selection)
                    if (matched_option_index >= 0) {
                        title = filter.options[matched_option_index]?.[1]
                    }
                }
            } else {
                // A toggle
                title = $t(filter.title)
            }

            return this.render_button(filter, title, null)
        }
    }

    view(vnode: m.Vnode<FiltersActiveAttrs>) {
        const filters_activated = activated_filters(vnode.attrs.collection)
        const naming_pending = filters_naming_pending(vnode.attrs.collection)
        return (
            <div className='c-filters-active'>
                {(() => {
                    if (filters_activated === 0 || naming_pending || !vnode.attrs.collection.state.ready) {
                        return <div class='inactive'>{naming_pending ? <Spinner size='l' /> : <Icon name='filterOff' />}</div>
                    }

                    return (
                        <div class='filters'>
                            {Object.values(vnode.attrs.collection.filters)
                                .filter((filter) => !filter.disabled)
                                .map((filter) => {
                                    return this.render_filter(filter)
                                })}
                        </div>
                    )
                })()}
            </div>
        )
    }
}
